import {
    CoreSwitchView,
    CoreTextView,
    type TCoreCheckboxOptions,
    type TCoreCheckboxStyleProps,
    type TCoreInputData,
    type TCoreTextStyleProps,
} from '@core/widgets';
import { TWidget, TWidgetCommonOption } from '@libs/types';
import clsx from 'clsx';
import React, { ChangeEventHandler } from 'react';
import { SemanticColor, semanticColors } from '../../theme/colors';
import { TextStyle } from '../../types/styles';
import styles from './checkBoxSwitcher.module.css';

const LABEL_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.main,
    main: semanticColors[SemanticColor.TextSecondary],
    disabled: { main: semanticColors[SemanticColor.TextPrimaryDisabled] },
    margin: { right: 24 },
};

const SWITCH_STYLE_PROPS: TCoreCheckboxStyleProps = {
    width: 48,
    height: 28,
    borderStroke: 0,
    borderRadius: 14,
    thumbSize: 24,
    thumbSizeChecked: 24,
    thumbOffset: 2,
    thumbOffsetChecked: 2,
    main: semanticColors[SemanticColor.FillPositive],
    mainContainer: semanticColors[SemanticColor.IconPrimaryOnDarkFill],
    outline: semanticColors[SemanticColor.IconPrimaryOnDarkFill],
    surfaceContainerHighest: semanticColors[SemanticColor.BgTertiary],
    pressed: { main: semanticColors[SemanticColor.FillPositivePressed], mainContainer: semanticColors[SemanticColor.IconPrimaryOnDarkFill] },
    disabled: {
        main: semanticColors[SemanticColor.FillPositiveDisabled],
        surfaceContainerHighest: semanticColors[SemanticColor.BorderPrimary],
        outline: semanticColors[SemanticColor.IconPrimaryOnDarkFill],
        mainContainer: semanticColors[SemanticColor.IconPrimaryOnDarkFill],
    },
};

export type TSwitcherViewProps = {
    uid?: string;
    widget?: TWidget<TCoreInputData, TCoreCheckboxOptions>;
    label?: string;
    isChecked: boolean;
    handleChange: ChangeEventHandler<HTMLInputElement>;
    isDisabled?: boolean;
    isHide?: boolean;
    margin?: TWidgetCommonOption['margin'];
    style?: string;
    parentStyleNames?: string[];
};

export const SwitcherView = ({ uid, widget, label, isChecked, handleChange, isDisabled, isHide, margin, style, parentStyleNames }: TSwitcherViewProps) => {
    const switchWidget: TWidget<TCoreInputData, TCoreCheckboxOptions> | undefined = widget
        ? { ...widget, options: { ...widget.options, margin: undefined } }
        : undefined;

    const wrapperStyle: React.CSSProperties = {
        ...(isHide ? { display: 'none' } : null),
        ...(margin?.top !== undefined ? { marginTop: `${margin.top}px` } : null),
        ...(margin?.right !== undefined ? { marginRight: `${margin.right}px` } : null),
        ...(margin?.bottom !== undefined ? { marginBottom: `${margin.bottom}px` } : null),
        ...(margin?.left !== undefined ? { marginLeft: `${margin.left}px` } : null),
    };

    return (
        <div data-test={uid} style={wrapperStyle} className={clsx(styles.switchWrapper, isDisabled && styles.disabled)}>
            <div className={styles.switchInputBlock}>
                {label && <CoreTextView as="span" text={label} isDisabled={isDisabled} styleProps={LABEL_STYLE_PROPS} />}
                <CoreSwitchView
                    widget={switchWidget}
                    uid={uid}
                    isChecked={isChecked}
                    handleChange={handleChange}
                    isDisabled={isDisabled}
                    isHide={isHide}
                    styleName={style ?? 'switchDefault'}
                    parentStyleNames={parentStyleNames}
                    styleProps={SWITCH_STYLE_PROPS}
                />
            </div>
        </div>
    );
};
